import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app.routes";
import {
    AuthenticationService,
    FORM_FILL_PARAMS,
    formFillParamsFactory,
    FormHelperService,
    UserIsAtLeastAdminProvider,
    UserIsOwnerProvider,
    UserService,
} from "./_services";
import { UserModule } from "./components/user/user.module";
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
} from "@angular/common/http";
import { ErrorInterceptor, JwtInterceptor } from "./interceptors";
import { AdminGuard, AuthGuard } from "./_guards";
import { SharedComponentsModule } from "./components/shared/shared-components.module";
import { ToastModule } from "primeng/toast";
import { MessageService } from "primeng/api";
import { AssessmentModule } from "@/components/assessment/assessment.module";
import { PagesModule } from "@/components/pages/pages.module";
import { MatExpansionModule } from "@angular/material/expansion";
import { AnonUserInterceptor } from "@/interceptors/AnonUserInterceptor";
import { AnonLandingComponent } from "./components/anon-landing/anon-landing.component";
import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { environment } from "../environments/environment";
import { DateUtils } from "./_utility/DateTimeFormat";
import { DialogService } from "primeng/dynamicdialog";
import { createInstance } from "@featurevisor/sdk";
import { setInstance } from "@features/instance";
import { catchError, firstValueFrom } from "rxjs";
import { map } from "rxjs/operators";

// Application wide providers
const AppProviders = [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AnonUserInterceptor, multi: true },
];

function initializeApp() {
    (window as any).pdfWorkerSrc = "/assets/pdfjs/pdf.worker.js";
    console.log("pdf worker url set");
}

function staleAccountInitializer(
    authenticationService: AuthenticationService,
): () => Promise<void> {
    // refreshAccountIfStale() already swallows its own errors internally.
    return () =>
        firstValueFrom(authenticationService.refreshAccountIfStale()).then(
            () => undefined,
        );
}

function featurevisorInitializer(http: HttpClient): () => Promise<void> {
    return async () => {
        try {
            const datafile = await firstValueFrom(
                http.get(environment.featurevisorDatafileUrl),
            );
            setInstance(createInstance({ datafile: datafile as any }));
        } catch (e) {
            console.error("failed to load featurevisor", e);

            setInstance(
                createInstance({
                    datafile: {
                        schemaVersion: "2",
                        revision: "0",
                        segments: {},
                        features: {},
                    },
                }),
            );
        }
    };
}

const sentry = environment.production
    ? [
          {
              provide: ErrorHandler,
              useValue: Sentry.createErrorHandler(),
          },
          {
              provide: Sentry.TraceService,
              deps: [Router],
          },
      ]
    : [];

@NgModule({
    bootstrap: [AppComponent],
    declarations: [AppComponent, AnonLandingComponent],
    imports: [
        // import Angular's modules
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        AppRoutingModule,
        UserModule,
        ReactiveFormsModule,
        SharedComponentsModule,
        ToastModule,
        AssessmentModule,
        PagesModule,
        MatExpansionModule,
    ],
    providers: [
        AppProviders,
        AuthenticationService,
        FormHelperService,
        UserService,
        AuthGuard,
        AdminGuard,
        DateUtils,
        UserIsAtLeastAdminProvider,
        UserIsOwnerProvider,
        DialogService,
        MessageService,
        {
            provide: FORM_FILL_PARAMS,
            useFactory: formFillParamsFactory,
            deps: [UserService],
        },
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: APP_INITIALIZER,
            useValue: initializeApp,
            deps: [Sentry.TraceService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: staleAccountInitializer,
            deps: [AuthenticationService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: featurevisorInitializer,
            deps: [HttpClient],
            multi: true,
        },
        ...sentry,
    ],
})
export class AppModule {}
